<template>
  <div>
    <section class="catalogue-banner-section">
      <content-creator
        :key="catalogueHeaderBlobID"
        :settings="getBlobContents(catalogueHeaderBlobID)"
        :permissions="contentCreatorPermissions"
        :is-visible="editContentCreator"
        :filestore="websitefilestore"
        :name="catalogueHeaderBlobID"
        class="header-carousel"
        allowed-widgets="carousel,content-list,content-grid"
        @updateBlob="(data) => saveBlob(catalogueHeaderBlobID, data)"
      />
    </section>
    <b-container>
      <b-row>
        <b-col sm="12">
          <route-catalog
            v-model="settings"
            :filters="WineCatalogFilters"
            :sorters="WineCatalogSorters"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
// import products from 'wine-catalog/src/mockdata/wines.js'
import applogger from '@applogger'
import catalogloader from '../wine-products/0001/winecatalog-preload-helper'
import contentCreatorMixin from 'src/app/content-creator-mixin'
import RouteCatalog from 'dev/vuejs-ecommerce-product-catalog/src/RouteCatalog'

function asyncLoader({ store, route }) {
  const __loader = () => {
    return Promise.all([
      catalogloader(store),
      store.dispatch('blob/load', { blobID: `catalog-list-banner--${route.meta.catalogueID}`, cacheOptions: { scope: 'global', ttl: 3600 * 12 } })
    ])
      .then(([catalogList, _blob]) => {
        store.dispatch('productCatalog/setProducts', catalogList)
        store.dispatch('productCatalog/loadLowStockThreshold')
        store.dispatch('productCatalog/loadProductStock')
        if (store.getters['blob/isHeader'](`catalog-list-banner--${route.meta.catalogueID}`)) {
          store.commit('SET_PAGE_SETTING', { key: 'hasHeader', value: true })
        }
        applogger.log('RouteCatalog asyncData Complete')
      })
  }

  return store.dispatch('promisePool', {
    name: 'route-catalogue',
    resolve: __loader
  })
}

export default {
  name: 'product-catalogue-page',
  headData: true,
  components: {
    RouteCatalog
  },
  mixins: [
    contentCreatorMixin
  ],
  asyncData: asyncLoader,
  computed: {
    catalogueHeaderBlobID: function() {
      return `catalog-list-banner--${this.$route.meta.catalogueID}`
    },
    catalogueID: function() {
      return this.$route.meta.catalogueID
    },
    WineCatalogSorters: function() {
      const defaultSorters = [
        {
          label: 'Default sort',
          sortBy: '',
          type: 'select'
        },
        {
          label: 'Sort by Price',
          sortBy: 'sellPrice',
          type: 'select',
          numeric: true
        },
        {
          label: 'Sort by Product Name',
          sortBy: 'title',
          type: 'select'
        },
        {
          label: 'Sort by Date added',
          sortBy: 'dateChanged',
          type: 'select',
          numeric: true
        }
      ]

      if (this.$store.getters.getModuleSetting('wine-catalog', 'tastingOrderSort', false)) {
        const fallback = this.$store.getters.getModuleSetting('wine-catalog', 'tastingOrderFallback', 'title')
        defaultSorters.push({
          label: 'Sort by Tasting Order',
          sortBy: 'wine_type',
          type: 'taxonomy',
          fallback
        })
      }

      return this.$store.getters.getModuleSetting('wine-catalog', 'sorters', defaultSorters)
    },
    WineCatalogFilters: function() {
      const defaultFilters = [
        {
          label: 'Wine Type',
          filterBy: 'wine_type',
          type: 'select'
        },
        {
          label: 'Series/Collection',
          filterBy: 'series',
          type: 'select'
        },
        {
          label: 'Varietals',
          filterBy: 'varietal',
          type: 'select'
        },
        {
          label: 'Brand',
          filterBy: 'brand',
          type: 'select'
        },
        {
          label: 'Collections',
          filterBy: 'collections',
          type: 'select'
        },
        {
          label: 'Attributes',
          filterBy: 'attributes',
          type: 'select'
        },
        {
          label: 'Region',
          filterBy: 'region',
          type: 'select'
        },
        // UnoVino only
        // {
        //   label: 'Producer',
        //   filterBy: 'producer',
        //   type: 'select'
        // },
        {
          label: 'Sweetness',
          filterBy: 'sweetness',
          type: 'range-slider',
          minLabel: 'Dry',
          maxLabel: 'Sweet'
        }
      ]
      return this.$store.getters.getModuleSetting('wine-catalog', 'filters', defaultFilters)
    },
    settings: {
      get: function() {
        return {
          filters: this.$store.state.productCatalog.filters,
          sorters: this.$store.state.productCatalog.sorters,
          sortDir: this.$store.state.productCatalog.sortDir,
          view: this.$store.getters['productCatalog/viewMode']
        }
      },
      set: function(value) {
        this.$store.dispatch('productCatalog/setCatalogFilters', value.filters)
        this.$store.dispatch('productCatalog/setCatalogSorter', value.sorters)
        this.$store.dispatch('productCatalog/setCatalogSortDirection', value.sortDir)
        this.$store.dispatch('productCatalog/setCatalogView', value.view)
      }
    }
  },
  watch: {
    '$route': {
      handler(route) {
        asyncLoader({ store: this.$store, route })
      },
      deep: true
    },
    '$route.query': {
      handler: function(updated, old) {
        const routeKeys = Object.keys(updated)
        if (routeKeys.length > 0) {
          const settings = JSON.parse(JSON.stringify(this.settings))
          const filterKeys = Object.keys(settings.filters)
          routeKeys.forEach((key) => {
            if (filterKeys.includes(key)) {
              settings.filters[key] = [updated[key]]
            }
          })
          this.settings = settings
        }
      }
    }
  },
  mounted() {
    if (this.$store.getters['blob/isHeader'](this.catalogueHeaderBlobID)) {
      this.$store.commit('SET_PAGE_SETTING', { key: 'hasHeader', value: true })
    }
  },
  created: function() {
    const filters = {
      dietary: [],
      wine_type: [],
      sweetness: [0, 5],
      series: [],
      varietal: [],
      varietals: [],
      collections: [],
      region: [],
      brand: [],
      attributes: [],
      price: [],
      producer: []
    }

    let defaultSort = this.$store.getters.getModuleSetting('wine-catalog', 'defaultSort', {})
    defaultSort = { order: '', direction: 'ASC', ...defaultSort }

    Promise.all([
      this.$store.dispatch('productCatalog/setCatalogFilters', filters),
      this.$store.dispatch('productCatalog/setCatalogSorter', defaultSort.order),
      this.$store.dispatch('productCatalog/setCatalogSortDirection', defaultSort.direction),
      this.$store.dispatch('productCatalog/setCatalogView', 'grid-view')
    ])
  }
}
</script>

<style>

</style>
